import React, { useState, useEffect } from "react";
import { Calendar, Pause } from "lucide-react";
import toast from "react-hot-toast";
import { isFutureDate } from "../../hooks/commonFunction";

const PauseSubscriptionModal = ({
  isOpen,
  onClose,
  onConfirm,
  subscriptionName,
  schedules = [],
  isSubmitting = false,
}) => {
  const [pauseReason, setPauseReason] = useState("");
  const [error, setError] = useState("");

  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    if (schedules && schedules.length > 0) {
      // Select the second future scheduled date by default
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const futureScheduledDates = schedules.filter(
        (s) => s.status === "scheduled" && new Date(s.scheduled_date) > today
      );

      // Select the 2nd date if available, otherwise select the first
      if (futureScheduledDates.length >= 2) {
        setSelectedDate(futureScheduledDates[1].scheduled_date);
      } else if (futureScheduledDates.length === 1) {
        setSelectedDate(futureScheduledDates[0].scheduled_date);
      }
    }
  }, [schedules]);

  // Reset reason and error when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setPauseReason("");
      setError("");
    }
  }, [isOpen]);

  const handleConfirm = () => {
    setError("");

    if (!selectedDate) {
      toast.error("Please select a delivery date");
      return;
    }

    if (!isFutureDate(selectedDate)) {
      toast.error("Please select a future date for delivery");
      return;
    }

    // Pass as object
    onConfirm({
      date: selectedDate,
      reason: pauseReason.trim(),
    });
  };

  const handleClose = () => {
    setPauseReason("");
    setError("");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg overflow-hidden max-w-md w-full mx-4">
        {/* Gradient Header */}
        <div className="bg-hf-gradient px-6 py-4 text-center">
          <h3 className="text-xl font-bold text-white">Pause Subscription</h3>
        </div>

        <div className="p-6">
          {/* Icon and Message */}
          <div className="flex flex-col items-center text-center mb-4">
            <p className="text-gray-600">
              {subscriptionName && (
                <span className="font-semibold">{subscriptionName}</span>
              )}
            </p>
          </div>

          {/* Reason Input */}
          <div className="mb-1">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Please tell us why you're pausing (optional):
            </label>
            <textarea
              value={pauseReason}
              onChange={(e) => {
                setPauseReason(e.target.value);
                if (error) setError("");
              }}
              placeholder="Enter your reason here..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-100 resize-none"
              rows={2}
              disabled={isSubmitting}
            />
          </div>
          {/* Date Selection */}
          {schedules && schedules.length > 0 && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Select Next Delivery Date:
              </label>
              <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-lg">
                {schedules
                  .filter((s) => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    return (
                      s.status === "scheduled" &&
                      new Date(s.scheduled_date) > today
                    );
                  })
                  .map((schedule) => (
                    <label
                      key={schedule.id}
                      className={`flex items-center p-3 cursor-pointer hover:bg-gray-50 border-b last:border-b-0 ${
                        selectedDate === schedule.scheduled_date
                          ? "bg-red-50 border-l-4 border-l-red-600"
                          : ""
                      }`}
                    >
                      <input
                        type="radio"
                        name="deliveryDate"
                        value={schedule.scheduled_date}
                        checked={selectedDate === schedule.scheduled_date}
                        onChange={() =>
                          setSelectedDate(schedule.scheduled_date)
                        }
                        className="w-4 h-4 text-red-600 focus:ring-red-500"
                      />
                      <span
                        className={`ml-3 text-sm ${
                          selectedDate === schedule.scheduled_date
                            ? "font-semibold text-red-700"
                            : "text-gray-700"
                        }`}
                      >
                        {new Date(schedule.scheduled_date).toLocaleDateString(
                          "en-US",
                          {
                            weekday: "short",
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          }
                        )}
                      </span>
                    </label>
                  ))}
              </div>
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2.5 bg-primary-100 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2.5 border-2 border-gray-400 text-gray-400 rounded-lg font-semibold hover:bg-gray-300 hover:text-black transition-colors disabled:opacity-50"
            >
              {isSubmitting ? "Pausing..." : "Confirm"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PauseSubscriptionModal;
